import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { API_BASE, api } from '../services/api';
import { callReducer, initialCallState } from './callReducer';
import type { CallState, CallType, RTCIceServerConfig, RemoteSignal } from './types';
import { usePeerCall } from './usePeerCall';


interface CallContextValue {
  state: CallState;
  iceServers: RTCIceServerConfig[];
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  startCall: (
    peerId: number,
    callType: CallType,
    peer?: { name?: string | null; avatarUrl?: string | null },
  ) => Promise<boolean>;
  acceptCall: () => Promise<boolean>;
  declineCall: () => boolean;
  hangup: (reason?: string) => boolean;
  toggleMute: () => void;
  toggleCamera: () => void;
  sendSignal: (event: Record<string, unknown>) => boolean;
  handlePeerConnected: () => boolean;
  reset: () => void;
}


const CallContext = createContext<CallContextValue | null>(null);


const socketOrigin = (): string => {
  const source = API_BASE || window.location.origin;
  const url = new URL(source, window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.origin;
};


const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);


export function CallProvider({ userId, children }: { userId: string | null; children: ReactNode }) {
  const [state, dispatch] = useReducer(callReducer, initialCallState);
  const [iceServers, setIceServers] = useState<RTCIceServerConfig[]>([]);
  const socketRef = useRef<WebSocket | null>(null);
  const stateRef = useRef(state);
  const signalSequenceRef = useRef(0);
  const processedSignalRef = useRef(0);
  const offeredCallRef = useRef<string | null>(null);
  const authGenerationRef = useRef(0);
  stateRef.current = state;

  const sendSignal = useCallback((event: Record<string, unknown>): boolean => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(event));
    return true;
  }, []);

  const onPeerConnected = useCallback(() => {
    const callId = stateRef.current.callId;
    if (!callId) return;
    if (sendSignal({ type: 'call.connected', call_id: callId })) {
      dispatch({ type: 'PEER_CONNECTED', callId, connectedAt: Date.now() });
    }
  }, [sendSignal]);

  const onPeerFailure = useCallback((reason: string) => {
    const callId = stateRef.current.callId;
    if (callId) sendSignal({ type: 'call.hangup', call_id: callId, reason });
    dispatch({ type: 'SERVER_ERROR', code: reason });
  }, [sendSignal]);

  const peerCall = usePeerCall({
    iceServers,
    sendSignal,
    onConnected: onPeerConnected,
    onFailure: onPeerFailure,
  });

  useEffect(() => {
    authGenerationRef.current += 1;
    if (!userId) {
      socketRef.current?.close();
      socketRef.current = null;
      setIceServers([]);
      dispatch({ type: 'LOGOUT' });
      return;
    }

    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectStartedAt = Date.now();
    let reconnectIndex = 0;
    const delays = [500, 1000, 2000, 4000];

    const handleServerEvent = (raw: string) => {
      let event: Record<string, unknown>;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed) || typeof parsed.type !== 'string') return;
        event = parsed;
      } catch {
        return;
      }

      if (event.type === 'socket.ping' && typeof event.nonce === 'string') {
        sendSignal({ type: 'socket.pong', nonce: event.nonce });
      } else if (
        event.type === 'call.ringing'
        && typeof event.call_id === 'string'
        && typeof event.callee_id === 'number'
        && (event.call_type === 'audio' || event.call_type === 'video')
      ) {
        dispatch({
          type: 'SERVER_RINGING', callId: event.call_id,
          peerId: event.callee_id, callType: event.call_type,
        });
      } else if (
        event.type === 'call.incoming'
        && typeof event.call_id === 'string'
        && typeof event.caller_id === 'number'
        && (event.call_type === 'audio' || event.call_type === 'video')
      ) {
        dispatch({
          type: 'SERVER_INCOMING', callId: event.call_id,
          peerId: event.caller_id, callType: event.call_type,
        });
      } else if (
        event.type === 'call.accepted'
        && typeof event.call_id === 'string'
        && (event.call_type === 'audio' || event.call_type === 'video')
      ) {
        dispatch({ type: 'SERVER_ACCEPTED', callId: event.call_id, callType: event.call_type });
      } else if (
        (event.type === 'signal.offer' || event.type === 'signal.answer' || event.type === 'signal.ice')
        && typeof event.call_id === 'string'
      ) {
        signalSequenceRef.current += 1;
        const signal: RemoteSignal = {
          sequence: signalSequenceRef.current,
          type: event.type,
          callId: event.call_id,
          ...(typeof event.sdp === 'string' ? { sdp: event.sdp } : {}),
          ...(isRecord(event.candidate) ? { candidate: event.candidate as RTCIceCandidateInit } : {}),
        };
        dispatch({ type: 'SERVER_SIGNAL', signal });
      } else if (event.type === 'call.ended' && typeof event.call_id === 'string') {
        dispatch({
          type: 'SERVER_ENDED', callId: event.call_id,
          reason: typeof event.reason === 'string' ? event.reason : null,
          state: typeof event.state === 'string' ? event.state : 'ended',
        });
      } else if (event.type === 'call.error' && typeof event.code === 'string') {
        dispatch({ type: 'SERVER_ERROR', code: event.code });
      }
    };

    const scheduleReconnect = (connect: () => Promise<void>) => {
      if (stopped || Date.now() - reconnectStartedAt >= 15000) return;
      const delay = delays[Math.min(reconnectIndex, delays.length - 1)];
      reconnectIndex += 1;
      reconnectTimer = setTimeout(() => { void connect(); }, delay);
    };

    const connect = async (): Promise<void> => {
      if (stopped || socketRef.current) return;
      try {
        const [{ data: ticketData }, { data: configData }] = await Promise.all([
          api.post<{ ticket: string }>('/calls/socket-ticket'),
          api.get<{ iceServers: RTCIceServerConfig[] }>('/calls/config'),
        ]);
        if (stopped) return;
        setIceServers(Array.isArray(configData.iceServers) ? configData.iceServers : []);
        const socket = new WebSocket(
          `${socketOrigin()}/api/v1/calls/ws?ticket=${encodeURIComponent(ticketData.ticket)}`,
        );
        socketRef.current = socket;
        socket.onopen = () => {
          reconnectStartedAt = Date.now();
          reconnectIndex = 0;
        };
        socket.onmessage = event => handleServerEvent(event.data);
        socket.onclose = () => {
          if (socketRef.current === socket) socketRef.current = null;
          scheduleReconnect(connect);
        };
        socket.onerror = () => socket.close();
      } catch {
        scheduleReconnect(connect);
      }
    };

    void connect();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const socket = socketRef.current;
      socketRef.current = null;
      socket?.close();
      dispatch({ type: 'LOGOUT' });
    };
  }, [sendSignal, userId]);

  useEffect(() => {
    if (
      state.phase !== 'connecting'
      || state.direction !== 'outgoing'
      || !state.callId
      || offeredCallRef.current === state.callId
    ) return;
    offeredCallRef.current = state.callId;
    void peerCall.createOffer(state.callId).catch(() => onPeerFailure('media_failed'));
  }, [onPeerFailure, peerCall, state.callId, state.direction, state.phase]);

  useEffect(() => {
    const signal = state.remoteSignal;
    if (!signal || signal.sequence <= processedSignalRef.current) return;
    processedSignalRef.current = signal.sequence;
    const operation = signal.type === 'signal.offer' && signal.sdp
      ? peerCall.handleOffer(signal.callId, signal.sdp)
      : signal.type === 'signal.answer' && signal.sdp
        ? peerCall.handleAnswer(signal.callId, signal.sdp)
        : signal.type === 'signal.ice' && signal.candidate
          ? peerCall.handleIce(signal.callId, signal.candidate)
          : Promise.resolve();
    void operation.catch(() => onPeerFailure('media_failed'));
  }, [onPeerFailure, peerCall, state.remoteSignal]);

  useEffect(() => {
    if (state.phase === 'ended' || state.phase === 'idle') {
      peerCall.cleanup();
      offeredCallRef.current = null;
    }
  }, [peerCall, state.phase]);

  const startCall = useCallback(async (
    peerId: number,
    callType: CallType,
    peer?: { name?: string | null; avatarUrl?: string | null },
  ) => {
    const generation = authGenerationRef.current;
    dispatch({ type: 'RESET' });
    dispatch({
      type: 'START_PREPARING', peerId, callType,
      peerName: peer?.name, peerAvatarUrl: peer?.avatarUrl,
    });
    try {
      await peerCall.prepareOutgoing(callType);
      if (authGenerationRef.current !== generation) {
        peerCall.cleanup();
        return false;
      }
      const sent = sendSignal({ type: 'call.start', callee_id: peerId, call_type: callType });
      if (!sent) {
        peerCall.cleanup();
        dispatch({ type: 'SERVER_ERROR', code: 'signaling_unavailable' });
      }
      return sent;
    } catch {
      if (authGenerationRef.current !== generation) return false;
      dispatch({ type: 'SERVER_ERROR', code: 'media_denied' });
      return false;
    }
  }, [peerCall, sendSignal]);

  const acceptCall = useCallback(async () => {
    const current = stateRef.current;
    if (!current.callId) return false;
    const generation = authGenerationRef.current;
    try {
      await peerCall.prepareIncoming(current.callType ?? 'audio');
      if (authGenerationRef.current !== generation) {
        peerCall.cleanup();
        return false;
      }
      const sent = sendSignal({ type: 'call.accept', call_id: current.callId });
      if (sent) dispatch({ type: 'LOCAL_ACCEPTED', callId: current.callId });
      return sent;
    } catch {
      if (authGenerationRef.current !== generation) return false;
      sendSignal({ type: 'call.hangup', call_id: current.callId, reason: 'media_denied' });
      dispatch({ type: 'SERVER_ERROR', code: 'media_denied' });
      return false;
    }
  }, [peerCall, sendSignal]);

  const declineCall = useCallback(() => {
    const callId = stateRef.current.callId;
    return callId ? sendSignal({ type: 'call.decline', call_id: callId }) : false;
  }, [sendSignal]);

  const hangup = useCallback((reason = 'hangup') => {
    const callId = stateRef.current.callId;
    if (!callId) return false;
    const sent = sendSignal({ type: 'call.hangup', call_id: callId, reason });
    peerCall.cleanup();
    return sent;
  }, [peerCall, sendSignal]);

  const handlePeerConnected = useCallback(() => {
    const callId = stateRef.current.callId;
    if (!callId) return false;
    const sent = sendSignal({ type: 'call.connected', call_id: callId });
    if (sent) dispatch({ type: 'PEER_CONNECTED', callId, connectedAt: Date.now() });
    return sent;
  }, [sendSignal]);

  const value = useMemo<CallContextValue>(() => ({
    state,
    iceServers,
    localStream: peerCall.localStream,
    remoteStream: peerCall.remoteStream,
    startCall,
    acceptCall,
    declineCall,
    hangup,
    toggleMute: () => {
      peerCall.setMuted(!state.muted);
      dispatch({ type: 'SET_MUTED', value: !state.muted });
    },
    toggleCamera: () => {
      if (state.callType !== 'video') return;
      peerCall.setCameraOff(!state.cameraOff);
      dispatch({ type: 'SET_CAMERA_OFF', value: !state.cameraOff });
    },
    sendSignal,
    handlePeerConnected,
    reset: () => dispatch({ type: 'RESET' }),
  }), [
    acceptCall, declineCall, handlePeerConnected, hangup, iceServers, peerCall,
    sendSignal, startCall, state,
  ]);

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}


export function useCall(): CallContextValue {
  const context = useContext(CallContext);
  if (!context) throw new Error('useCall must be used inside CallProvider');
  return context;
}
